import { useEffect, useState, useRef } from "react";
// import axios from "axios";
import { jwtDecode } from "jwt-decode";
// eslint-disable-next-line no-unused-vars
import { FiSend, FiPaperclip } from "react-icons/fi"; // ✅ Icons
import socket from "../../utils/socket";

const ChatBox = ({ groupId, chatTitle, onClose, visible }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState("");
  const [mentionList, setMentionList] = useState([]);
  const [mentionDropdown, setMentionDropdown] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [attachment, setAttachment] = useState(null); // ✅ For file

  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("token");
  const storedUser = jwtDecode(token);

  const currentUser = {
    userId: storedUser?.userId,
    type: storedUser?.roles,
    name: storedUser?.userName,
    buyerId: storedUser?.buyerId,
    receiverIds: storedUser?.associated,
  };

  const REACT_APP_API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:9001";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!socket.connected) socket.connect();
    socket.emit("joinRoom", { groupId });

    // const fetchMessages = async () => {
    //   try {
    //     const res = await axios.post(
    //       `${REACT_APP_API_BASE_URL}socket/api/getMessagesBygroupId`,
    //       { groupId },
    //       { headers: { Authorization: `Bearer ${token}` } }
    //     );
    //     const msgs = Array.isArray(res.data) ? res.data : res.data?.messages || [];
    //     setMessages(msgs);
    //     scrollToBottom();
    //   } catch (error) {
    //     console.error("Fetch error:", error.message);
    //   }
    // };

    const fetchMessages = async () => {
      try {
        const response = await fetch(
          `${REACT_APP_API_BASE_URL}socket/api/getMessagesBygroupId`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ groupId }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const msgs = Array.isArray(data) ? data : data?.messages || [];
        setMessages(msgs);
        scrollToBottom();
      } catch (error) {
        console.error("Fetch error:", error.message);
      }
    };

    // const fetchMentionUser = async () => {
    //   try {
    //     const res = await axios.post(
    //       `${REACT_APP_API_BASE_URL}socket/api/fetchMentionUser`,
    //       { senderId: currentUser.userId },
    //       { headers: { Authorization: `Bearer ${token}` } }
    //     );
    //     setMentionList(res.data);
    //   } catch (error) {
    //     console.error("Mention user fetch error:", error.message);
    //   }
    // };

    const fetchMentionUser = async () => {
      try {
        const response = await fetch(
          `${REACT_APP_API_BASE_URL}socket/api/fetchMentionUser`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ senderId: currentUser.userId }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setMentionList(data);
      } catch (error) {
        console.error("Mention user fetch error:", error.message);
      }
    };

    fetchMessages();
    fetchMentionUser();

    const handleNewMessage = (msg) => {
      if (msg.groupId === groupId) fetchMessages();
    };

    const handleTyping = ({ userName, chatId }) => {
      if (groupId === chatId && userName !== currentUser.name) {
        setTypingUser(userName);
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("typing", handleTyping);

    return () => {
      socket.emit("leaveRoom", groupId);
      socket.off("newMessage", handleNewMessage);
      socket.off("typing", handleTyping);
    };
  }, [
    REACT_APP_API_BASE_URL,
    currentUser.name,
    currentUser.userId,
    groupId,
    token,
  ]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    socket.emit("typing", { userName: currentUser.name, groupId });

    const mentionMatch = value.match(/@(\w*)$/);
    if (mentionMatch) {
      const query = mentionMatch[1]?.toLowerCase();
      const filtered = mentionList.filter((u) =>
        u.name.toLowerCase().startsWith(query)
      );
      setMentionDropdown(filtered);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleMentionSelect = (name) => {
    const newText = input.replace(/@(\w*)$/, `@${name} `);
    setInput(newText);
    setShowDropdown(false);
  };

  const handleSend = () => {
    if (!input.trim() && !attachment) return;

    const newMsg = {
      groupId,
      chatId: groupId,
      senderId: currentUser.userId,
      senderType: currentUser.type,
      senderName: currentUser.name,
      receiverIds: currentUser.receiverIds,
      message: input,
      attachment: attachment ? attachment.name : null, // Stub, handle upload if needed
      timestamp: new Date().toISOString(),
    };

    socket.emit("sendMessage", newMsg);
    setMessages((prev) => [...prev, newMsg]);
    setInput("");
    setAttachment(null);
    scrollToBottom();
  };

  // eslint-disable-next-line no-unused-vars
  const handleAttachment = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const highlightIfMentioned = (msg) => msg.includes(`@${currentUser.name}`);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    let h = date.getHours();
    const m = date.getMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  };

  return (
    <div
      style={{
        ...styles.wrapper,
        transform: visible ? "translateX(0%)" : "translateX(100%)",
      }}
    >
      <div style={styles.header}>
        <span>
          💬 Chat: {chatTitle} #{groupId}
        </span>
        <button onClick={onClose} style={styles.closeBtn}>
          ×
        </button>
      </div>

      <div style={styles.body}>
        {messages.map((msg, i) => {
          const isSender = msg.senderId === currentUser.userId;
          const highlight = highlightIfMentioned(msg.message);
          return (
            <div
              key={i}
              style={{
                ...styles.messageRow,
                justifyContent: isSender ? "flex-end" : "flex-start",
              }}
            >
              <div
                style={{
                  ...styles.messageBubble,
                  backgroundColor: highlight
                    ? "#fff8b3"
                    : isSender
                    ? "#d9fdd3"
                    : "#f0f0f0",
                }}
              >
                <div style={styles.sender}>
                  {isSender ? "You" : msg.senderName}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: msg.message.replace(
                      /@(\w+)/g,
                      '<span style="color:#0b93f6;font-weight:bold;">@$1</span>'
                    ),
                  }}
                />
                {msg.attachment && (
                  <div style={{ marginTop: 6 }}>
                    
                    <span style={{ color: "#0077cc" }}>
                      {msg.attachment}
                    </span>
                    
                  </div>
                )}
                <div style={styles.timestamp}>{formatTime(msg.timestamp)}</div>
              </div>
            </div>
          );
        })}

        {isTyping && <div style={styles.typing}>{typingUser} is typing...</div>}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputArea}>
        <div style={styles.inputWrapper}>
          <input
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            style={styles.input}
          />
          {/* <label htmlFor="file-upload" style={styles.attachmentIcon}>
            <FiPaperclip size={18} />
            <input
              id="file-upload"
              type="file"
              style={{ display: "none" }}
              onChange={handleAttachment}
            />
          </label> */}
          <button onClick={handleSend} style={styles.sendBtn}>
            <FiSend size={18} />
          </button>
        </div>
        {showDropdown && (
          <div style={styles.dropdown}>
            {mentionDropdown.map((u) => (
              <div
                key={u.userId}
                onClick={() => handleMentionSelect(u.name)}
                style={styles.dropdownItem}
              >
                @{u.name}
              </div>
            ))}
          </div>
        )}
        {attachment && (
          <div style={styles.attachmentPreview}>📎 {attachment.name}</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    position: "fixed",
    right: "400px",
    bottom: 20,
    width: 380,
    height: 600,
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    zIndex: 9999,
    transform: "translateX(100%)",
    transition: "transform 0.5s ease-in-out",
  },
  header: {
    background: "#ededed",
    padding: "10px 15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: "bold",
    borderBottom: "1px solid #ccc",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
  },
  body: {
    flex: 1,
    padding: "10px",
    overflowY: "auto",
  },
  messageRow: {
    display: "flex",
    marginBottom: 10,
  },
  messageBubble: {
    maxWidth: "75%",
    padding: "8px 12px",
    borderRadius: "10px",
    lineHeight: 1.4,
    fontSize: "14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  sender: {
    fontWeight: "bold",
    marginBottom: 2,
  },
  timestamp: {
    fontSize: "10px",
    textAlign: "right",
    marginTop: 4,
    color: "#999",
  },
  typing: {
    fontStyle: "italic",
    fontSize: "12px",
    color: "#555",
    margin: "5px 0",
  },
  inputArea: {
    borderTop: "1px solid #eee",
    padding: "10px",
    position: "relative",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
  },
  input: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: "20px",
    border: "1px solid #ccc",
    outline: "none",
    fontSize: "14px",
  },
  sendBtn: {
    background: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    padding: "8px",
    marginLeft: "6px",
    cursor: "pointer",
  },
  attachmentIcon: {
    marginLeft: "6px",
    cursor: "pointer",
    color: "#555",
  },
  dropdown: {
    position: "absolute",
    bottom: "45px",
    left: "10px",
    right: "10px",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: "6px",
    maxHeight: "120px",
    overflowY: "auto",
    zIndex: 2000,
  },
  dropdownItem: {
    padding: "8px 12px",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
  },
  attachmentPreview: {
    marginTop: "6px",
    fontSize: "12px",
    color: "#333",
  },
};

export default ChatBox;
